// @ts-nocheck
import { DEFAULT_TABLE_SCHEMA_CONFIG } from '../../../const/CommonConst';
import { getColumnName, parseCellId } from '../../../utils/Utils';

export const initialState = {
  schema: {
    rows: 6,
    columns: 10,
    cells: {}, // Each cell will have: { value: '', valueType: 'default', formula: '' }
    columnSettings: {},
    rowSettings: {},
    mergedCells: [],
  },
  ui: {
    selectedCells: [],
    currentCell: null,
    isSelecting: false,
    anchorCell: null,
    editMode: false,
    clipboard: null,
    showFormulaEditor: false,
    editorWidth: 430,
    showConditionalFormattingDrawer: false,
  },
  config: DEFAULT_TABLE_SCHEMA_CONFIG,
  constantId: null,
};

export function tableReducer(state, action) {
  switch (action.type) {
    case 'SET_CONFIG':
      return {
        ...state,
        config: {
          ...state.config,
          ...action.payload,
        },
        constantId: action.payload?.constantId || state.constantId,
      };

    case 'SET_SCHEMA':
      return {
        ...state,
        schema: {
          ...action.payload,
        },
      };

    case 'UPDATE_CELL_VALUE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema?.cells,
            [action.payload.cellId]: {
              ...state.schema?.cells?.[action.payload.cellId],
              value: action.payload.value,
            },
          },
        },
      };

    case 'SET_CURRENT_CELL':
      return {
        ...state,
        ui: {
          ...state.ui,
          currentCell: action.payload,
          editMode: true,
          showConditionalFormattingDrawer: false,
        },
      };

    case 'UPDATE_SELECTION':
      return {
        ...state,
        ui: {
          ...state.ui,
          selectedCells: action.payload.cells,
          currentCell: action.payload.currentCell,
          anchorCell: action.payload.anchorCell || state.ui.anchorCell,
        },
      };

    case 'UPDATE_SCHEMA':
      return {
        ...state,
        schema: action.payload,
      };

    case 'UPDATE_COLUMN_WIDTH':
      return {
        ...state,
        schema: {
          ...state.schema,
          columnSettings: {
            ...state.schema.columnSettings,
            [action.payload.columnName]: {
              ...state.schema.columnSettings[action.payload.columnName],
              width: action.payload.width,
            },
          },
        },
      };

    case 'UPDATE_ROW_HEIGHT':
      return {
        ...state,
        schema: {
          ...state.schema,
          rowSettings: {
            ...state.schema?.rowSettings,
            [action.payload.rowIndex]: {
              ...state.schema?.rowSettings?.[action.payload.rowIndex],
              height: action.payload.height,
            },
          },
        },
      };

    case 'START_SELECTION':
      return {
        ...state,
        ui: {
          ...state.ui,
          isSelecting: true,
          anchorCell: action.cellId,
          currentCell: action.cellId,
          selectedCells: [action.cellId],
          editMode: true,
        },
      };

    case 'END_SELECTION':
      return {
        ...state,
        ui: {
          ...state.ui,
          isSelecting: false,
        },
      };

    case 'SET_CLIPBOARD':
      return {
        ...state,
        ui: {
          ...state.ui,
          clipboard: action.payload,
        },
      };

    case 'PASTE_CELLS':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema?.cells,
            ...action.payload,
          },
        },
      };

    case 'ADD_PAGINATED_CELLS':
      // Incrementally add cells from pagination without replacing existing ones
      return {
        ...state,
        schema: {
          ...state.schema,
          // Update dimensions if the new schema has larger values
          rows: Math.max(state.schema.rows, action.payload.rows || 0),
          columns: Math.max(state.schema.columns, action.payload.columns || 0),
          // Merge cells, but preserve locally edited cells over paginated data
          cells: {
            ...action.payload.cells, // Paginated data first
            ...state.schema?.cells, // Local edits override paginated data
          },
          // Merge other settings
          columnSettings: {
            ...state.schema?.columnSettings,
            ...action.payload.columnSettings,
          },
          rowSettings: {
            ...state.schema?.rowSettings,
            ...action.payload.rowSettings,
          },
          // Merge merged cells arrays
          mergedCells: [
            ...(state.schema?.mergedCells || []),
            ...(action.payload.mergedCells || []).filter(
              (newMerged) =>
                !state.schema?.mergedCells?.some(
                  (existingMerged) =>
                    JSON.stringify(existingMerged) === JSON.stringify(newMerged)
                )
            ),
          ],
        },
      };

    case 'SET_EDIT_MODE':
      return {
        ...state,
        ui: {
          ...state.ui,
          editMode: action.payload,
        },
      };

    case 'SET_ANCHOR_CELL':
      return {
        ...state,
        ui: {
          ...state.ui,
          anchorCell: action.payload,
        },
      };

    case 'UPDATE_CELL_FORMAT':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              format: {
                ...state.schema.cells[action.payload.cellId]?.format,
                ...action.payload.format,
              },
            },
          },
        },
      };

    case 'TOGGLE_CELL_LOCK':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              isLocked: !state.schema.cells[action.payload.cellId]?.isLocked,
            },
          },
        },
      };

    case 'UPDATE_CELL_VALUE_TYPE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              valueType: action.payload.valueType,
              value: '',
              formula: '',
            },
          },
        },
      };

    case 'UPDATE_CELL_FORMULA':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              formula: action.payload.formula,
            },
          },
        },
      };

    case 'SET_SHOW_FORMULA_EDITOR':
      return {
        ...state,
        ui: {
          ...state.ui,
          showFormulaEditor: action.payload,
          ...(action.payload ? { showConditionalFormattingDrawer: false } : {}),
        },
      };

    case 'SET_EDITOR_WIDTH':
      return {
        ...state,
        ui: {
          ...state.ui,
          editorWidth: action.payload,
        },
      };

    case 'UPDATE_CELL_DATA_TYPE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              dataType: action.payload.dataType,
              value: '', // Reset value when changing data type
            },
          },
        },
      };

    // Handle conditional formatting rules
    case 'ADD_CONDITIONAL_RULE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              conditionalRules: [
                ...(state.schema.cells[action.payload.cellId]
                  ?.conditionalRules || []),
                action.payload.rule,
              ],
            },
          },
        },
      };

    case 'UPDATE_CONDITIONAL_RULE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              conditionalRules: state.schema.cells[
                action.payload.cellId
              ]?.conditionalRules.map((rule) =>
                rule.id === action.payload.ruleId
                  ? { ...rule, ...action.payload.updates }
                  : rule
              ),
            },
          },
        },
      };

    case 'DELETE_CONDITIONAL_RULE':
      return {
        ...state,
        schema: {
          ...state.schema,
          cells: {
            ...state.schema.cells,
            [action.payload.cellId]: {
              ...state.schema.cells[action.payload.cellId],
              conditionalRules: state.schema.cells[
                action.payload.cellId
              ]?.conditionalRules.filter(
                (rule) => rule.id !== action.payload.ruleId
              ),
            },
          },
        },
      };

    case 'SET_OPEN_CONDITIONAL_FORMATTING_DRAWER':
      return {
        ...state,
        ui: {
          ...state.ui,
          showConditionalFormattingDrawer: true,
          showFormulaEditor: false,
        },
      };

    case 'SET_CLOSE_CONDITIONAL_FORMATTING_DRAWER':
      return {
        ...state,
        ui: {
          ...state.ui,
          showConditionalFormattingDrawer: false,
        },
      };

    // Config Column actions
    case 'SET_COLUMN_ATTRIBUTE_CONFIG':
      return {
        ...state,
        schema: {
          ...state.schema,
          columnSettings: {
            ...state.schema.columnSettings,
            [action.payload.columnName]: {
              ...state.schema.columnSettings[action.payload.columnName],
              attribute_config: action.payload.attribute_config,
            },
          },
        },
      };

    case 'REMOVE_CONFIG_COLUMN': {
      const removeColName = action.payload.columnName;
      const removeColIndex = parseCellId(`${removeColName}1`)[0];
      if (!removeColIndex) return state;
      // Allow removing even the last column — results in 0 columns (empty state)

      const newCells = {};
      const newColumnSettings = {};

      // Re-sequence: shift columns after the removed one
      Object.entries(state.schema.cells || {}).forEach(([cellId, cellData]) => {
        const [colIdx, rowNum] = parseCellId(cellId);
        if (!colIdx || !rowNum) return;

        if (colIdx < removeColIndex) {
          newCells[cellId] = cellData;
        } else if (colIdx > removeColIndex) {
          const newColLetter = getColumnName(colIdx - 1);
          newCells[`${newColLetter}${rowNum}`] = cellData;
        }
        // Skip cells in the removed column
      });

      // Re-sequence column settings
      for (let i = 1; i <= state.schema.columns; i += 1) {
        const colName = getColumnName(i);
        if (i < removeColIndex) {
          newColumnSettings[colName] =
            state.schema.columnSettings[colName] || {};
        } else if (i > removeColIndex) {
          const newColName = getColumnName(i - 1);
          newColumnSettings[newColName] =
            state.schema.columnSettings[colName] || {};
        }
        // Skip removed column
      }

      return {
        ...state,
        schema: {
          ...state.schema,
          columns: state.schema.columns - 1,
          cells: newCells,
          columnSettings: newColumnSettings,
        },
      };
    }

    case 'REORDER_CONFIG_COLUMN': {
      const { dragIndex, hoverIndex } = action.payload;
      if (dragIndex === hoverIndex) return state;

      const numCols = state.schema.columns;
      const originalOrder = Array.from({ length: numCols }, (_, i) =>
        getColumnName(i + 1)
      );

      const newOrder = [...originalOrder];
      // remove from dragIndex
      const [movedCol] = newOrder.splice(dragIndex, 1);
      // insert at hoverIndex
      newOrder.splice(hoverIndex, 0, movedCol);

      const newCells = {};
      const newColumnSettings = {};

      // Re-map column settings and cells
      newOrder.forEach((oldColName, newColIndex0Based) => {
        const newColName = getColumnName(newColIndex0Based + 1);

        // Settings
        if (state.schema.columnSettings[oldColName]) {
          newColumnSettings[newColName] =
            state.schema.columnSettings[oldColName];
        }

        // Cells
        const oldColIdx = parseCellId(`${oldColName}1`)[0];

        Object.entries(state.schema.cells || {}).forEach(
          ([cellId, cellData]) => {
            const [colIdx, rowNum] = parseCellId(cellId);
            if (colIdx === oldColIdx) {
              newCells[`${newColName}${rowNum}`] = cellData;
            }
          }
        );
      });

      return {
        ...state,
        schema: {
          ...state.schema,
          cells: newCells,
          columnSettings: newColumnSettings,
        },
      };
    }

    case 'ADD_CONFIG_COLUMN': {
      const newColIndex = state.schema.columns + 1;
      const newColName = getColumnName(newColIndex);
      return {
        ...state,
        schema: {
          ...state.schema,
          columns: newColIndex,
          columnSettings: {
            ...state.schema.columnSettings,
            [newColName]: {
              width: 100,
              attribute_config: {
                attribute_id: '',
                attribute_schema: {},
              },
            },
          },
        },
      };
    }

    case 'ADD_CONFIG_COLUMN_WITH_ATTRIBUTE': {
      const { attribute } = action.payload;
      const newColIndex = state.schema.columns + 1;
      const newColName = getColumnName(newColIndex);
      return {
        ...state,
        schema: {
          ...state.schema,
          columns: newColIndex,
          columnSettings: {
            ...state.schema.columnSettings,
            [newColName]: {
              width: 150,
              attribute_config: {
                attribute_id: String(attribute?.id || ''),
                attribute_label: attribute?.label || attribute?.name || '',
                // Store local display data (not sent in save payload)
                _local_name: attribute?.name || '',
                _local_data_type: attribute?.data_type || '',
                attribute_schema: {},
              },
            },
          },
        },
      };
    }

    // Add other cases as needed

    default:
      return state;
  }
}
